import type { FC } from 'react';
import { Stack, styled, TextField, Typography } from '@mui/material';
import { Button, Modal, ModalInner } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';

export interface CancelDirectDebitMangopayModalViewProps {
  open: boolean;
  handleClose: () => void;
  isSuccess: boolean;
  isPending: boolean;
  isFamilyMember?: boolean;
  msg: string;
  onMsgChange: (value: string) => void;
  onSubmitRequest: () => void;
}

export const CancelDirectDebitMangopayModalView: FC<
  CancelDirectDebitMangopayModalViewProps
> = ({
  open,
  handleClose,
  isSuccess,
  isPending,
  isFamilyMember = false,
  msg,
  onMsgChange,
  onSubmitRequest,
}) => {
  const { t } = useTranslation();
  return (
    <Modal open={open} handleClose={handleClose}>
      <ModalInner>
        <CancelModalContainer>
          {!isSuccess ? (
            <>
              <Typography color="primary" fontWeight={700} sx={{ mb: 1 }}>
                {t('direct-debit.cancellation-request')}
              </Typography>

              {isFamilyMember && (
                <Stack sx={{ mb: 2, mt: 1 }}>
                  <Typography
                    color="warning.main"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                  >
                    {t(
                      'mangopay.direct-debit.cancel.warning-all-residents-title'
                    )}
                  </Typography>
                  <Typography color="grey.700" fontSize={14}>
                    {t(
                      'mangopay.direct-debit.cancel.warning-all-residents-description'
                    )}
                  </Typography>
                </Stack>
              )}

              <Stack>
                <Typography color="grey.800" fontSize={16} sx={{ mb: 0.2 }}>
                  {t('direct-debit.reason-of-cancellation')}
                </Typography>
                <TextField
                  value={msg}
                  onChange={(e) => onMsgChange(e.target.value)}
                  placeholder={t('direct-debit.cancel-eg-reason')}
                  multiline
                  rows={4}
                />
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-end"
                gap={1}
                sx={{ mt: 2 }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClose}
                >
                  {t('direct-debit.I-do-not-want-to-cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  loading={isPending}
                  onClick={onSubmitRequest}
                >
                  {t('direct-debit.submit-cancellation-request')}
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography color="success" fontWeight={600} sx={{ mb: 2 }}>
                {t('direct-debit.successfully-cancelled-request')}
              </Typography>
              <Typography color="info" fontWeight={600} sx={{ mb: 1 }}>
                {t('direct-debit.nursing-home-will-contact-you')}
              </Typography>
            </>
          )}
        </CancelModalContainer>
      </ModalInner>
    </Modal>
  );
};

const CancelModalContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    width: 450,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 2,
    borderRadius: 2,
  })
);
